import React, { type PropsWithChildren, createContext, useContext } from "react"

import type { CompanyStatus } from "@/graphql/codegen/graphql"
import { useCompanyStore } from "@/screens/Companies/stores"
import { useCompanyTypeFlags } from "@/screens/CompanySettings/Accounting/hooks/useCompanyTypeFlags"

interface PaymentTermsContextType {
  isCompanyVendor: boolean
  isCompanyCustomer: boolean
  companyStatus?: CompanyStatus
}

const PaymentTermsContext = createContext<PaymentTermsContextType | undefined>(undefined)

export const usePaymentTermsContext = (): {
  isCompanyVendor: boolean
  isCompanyCustomer: boolean
  companyStatus?: CompanyStatus
} => {
  const context = useContext(PaymentTermsContext)
  if (!context) {
    throw new Error("usePaymentTermsContext must be used within a PaymentTermsProvider")
  }
  return context
}

export const PaymentTermsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const company = useCompanyStore((state) => state.company)
  const { isCompanyVendor, isCompanyCustomer } = useCompanyTypeFlags()

  const value: PaymentTermsContextType = {
    isCompanyVendor,
    isCompanyCustomer,
    companyStatus: company?.status || undefined,
  }

  return <PaymentTermsContext.Provider value={value}>{children}</PaymentTermsContext.Provider>
}
